import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get } from 'lodash-es';
import Button from 'reactstrap/lib/Button';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { PageModel } from 'client/data/models/page';
import {
  buildTrimSummariesPath,
  InventoryCoreConfigurationsModel,
  InventoryTrimSummaryEntities,
} from 'client/data/models/inventory-core-configurations';
import { VehicleEntities } from 'client/data/models/vehicle-v2';
import { VisitorEntities } from 'client/data/models/visitor';
import { LEAD_FORMS_CREATIVE_ID } from 'site-modules/shared/components/lead-form/unified-lead-form/constants/creative-ids';
import { isUnknownZip } from 'site-modules/shared/utils/location';
import { getParamsFromVehicle } from 'site-modules/shared/utils/core-page/params';
import { getLeadFormCreativeId } from 'site-modules/shared/components/lead-form/utils/lead-form-creative-id';
import { getLeadFormCta, getTrimsWithLabels } from 'site-modules/shared/utils/core-page/trims-cta-utils';
import { getSuggestedDisplayPriceFromTrim } from 'site-modules/shared/utils/core-page/suggested-price';
import { TransparentPricingLeadFormDrawer } from 'site-modules/shared/components/lead-form/transparent-pricing-lead-form/transparent-pricing-lead-form-drawer';

function LeadFormButtonUI({
  vehicle,
  trims = null,
  sharedTrimName = null,
  visitorLocation = null,
  pageName = null,
  isMobile = false,
}) {
  const [isLeadFormOpen, toggleLeadFormOpen] = useToggle(false);
  const { zipCode, city } = visitorLocation;
  const isInternational = isUnknownZip(visitorLocation);

  if (!trims) {
    return null;
  }

  const currentTrim = trims.find(({ trimName }) => trimName === sharedTrimName) || get(trims, '[0]');
  const { styleId, trimName } = currentTrim;
  const ctaLabel = getLeadFormCta(isInternational);
  const displayPrice = getSuggestedDisplayPriceFromTrim(currentTrim);
  const showLeadForm = !!currentTrim?.hasLeadFormDealers || isInternational;

  const leadFormCreativeId = getLeadFormCreativeId({
    isNewVin: true,
    pageNameLegacy: pageName,
    formName: LEAD_FORMS_CREATIVE_ID.DOCK_TOP,
  });

  return (
    <Fragment>
      {showLeadForm && (
        <Button
          onClick={toggleLeadFormOpen}
          color="blue-50"
          className="px-1 py-0_5 size-16 rounded-8 fw-medium text-transform-none"
          data-tracking-id="view_pricing_details"
        >
          {ctaLabel}
        </Button>
      )}
      <TransparentPricingLeadFormDrawer
        isOpen={isLeadFormOpen}
        onDrawerToggle={toggleLeadFormOpen}
        params={{
          styleId,
          trim: trimName,
          vehicle,
          zipCode,
          city,
        }}
        options={{
          isMobile,
          bestDealPrice: displayPrice,
          creativeId: leadFormCreativeId,
        }}
        creativeId={leadFormCreativeId}
      />
    </Fragment>
  );
}

LeadFormButtonUI.propTypes = {
  vehicle: VehicleEntities.MakeModelSubmodelYear.isRequired,
  trims: InventoryTrimSummaryEntities.TrimSummaries,
  sharedTrimName: PropTypes.string,
  visitorLocation: VisitorEntities.Location,
  pageName: PropTypes.string,
  isMobile: PropTypes.bool,
};

const mapStateToProps = state => ({
  visitorLocation: get(state, 'visitor.location'),
  pageName: get(state, 'pageContext.legacy.pageName') || get(state, 'pageContext.page.name'),
});

export const propsAreEqual = (prevProps, { trims }) => !trims;
export const LeadFormButton = connect(mapStateToProps)(
  connectToModel(React.memo(LeadFormButtonUI, propsAreEqual), {
    sharedTrimName: bindToPath('sharedValues.trimName', PageModel),
    trims: bindToPath(
      ({ vehicle, isDealerlessOem }) => !isDealerlessOem && buildTrimSummariesPath(getParamsFromVehicle(vehicle)),
      InventoryCoreConfigurationsModel,
      getTrimsWithLabels
    ),
  })
);
